import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import i18next from 'i18next';
import { store } from '../store';
import { handleError } from './handleError';
import cookies from 'js-cookie';
import { SupportedCities } from '../enums';
import { UserSlice } from '../Auth/store';

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:3434';
const SERVER_NOT_RESPONDING_MSG = 'The server took too long to respond.';
const TIMEOUT = 6000;

const apiClient = axios.create({
  baseURL: apiBaseUrl,
});

// The backend used to rotate an expiring access token inside AuthenticationMiddleware and hand
// the new pair back on `c-access-token` / `c-refresh-token` response headers, so the session
// renewed itself on any request. Passport does no such thing: an expired token is simply a 401,
// and the new pair has to be fetched from POST /customer/refresh with the refresh token as the
// bearer credential.
//
// A page can fire several requests at once, so a burst of 401s must produce one refresh, not one
// per request. Everyone waits on the same in-flight promise.
let refreshInFlight: Promise<boolean> | null = null;

const requestNewTokens = async (): Promise<boolean> => {
  const { refreshToken } = store.getState().auth;

  if (!refreshToken) {
    return false;
  }

  try {
    // Deliberately not apiClient: that would run this interceptor again and recurse.
    const { data } = await axios.post(new URL('/customer/refresh', apiBaseUrl).toString(), undefined, {
      headers: { Authorization: `Bearer ${refreshToken}` },
      timeout: TIMEOUT,
    });

    if (!data?.token || !data?.refreshToken) {
      return false;
    }

    store.dispatch(UserSlice.actions.updateTokens({ token: data.token, refreshToken: data.refreshToken }));
    return true;
  } catch (err) {
    return false;
  }
};

const refreshSession = (): Promise<boolean> => {
  if (!refreshInFlight) {
    refreshInFlight = requestNewTokens().finally(() => {
      refreshInFlight = null;
    });
  }

  return refreshInFlight;
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    handleError('AXIOS_INTERCEPTOR', error.message);

    if (error.code === 'ERR_NETWORK') {
      return {
        data: { message: SERVER_NOT_RESPONDING_MSG },
        status: error.status,
      };
    }

    const failedRequest = error.config as (AxiosRequestConfig & { _retriedAfterRefresh?: boolean }) | undefined;
    const status = error.response?.status ?? error.status;

    // Only for a session that actually holds a refresh token. Guests hit 401s too, and their
    // state must not be cleared out from under them.
    if (status === 401 && failedRequest && !failedRequest._retriedAfterRefresh && store.getState().auth.refreshToken) {
      failedRequest._retriedAfterRefresh = true;

      if (await refreshSession()) {
        // By the time a request fails, axios has replaced config.headers with an AxiosHeaders
        // instance, which does not survive an object spread. Set through its API when present.
        const headers = failedRequest.headers as unknown as { set?: (k: string, v: string) => void };
        const authorization = `Bearer ${store.getState().auth.token}`;

        if (typeof headers?.set === 'function') {
          headers.set('Authorization', authorization);
        } else {
          failedRequest.headers = { ...(failedRequest.headers ?? {}), Authorization: authorization };
        }

        return apiClient.request(failedRequest);
      }

      // The refresh token is spent or rejected, so the session is genuinely over. Clear it
      // rather than leaving a half-alive one that 401s on every subsequent request.
      store.dispatch(UserSlice.actions.logoutReducer());
    }

    return {
      data: error.response?.data,
      status: error.status,
    };
  }
);
/**
 * The `fetch` wrapper fit for the app's needs.
 * @param path endpoint pathname (without host)
 * @param requestInit the second argument of the `fetch`
 * @param timeout optional timeout in milliseconds (defaults to TIMEOUT)
 * @returns
 */
const appRequest = async (path: string, requestInit?: AxiosRequestConfig, timeout?: number): Promise<AxiosResponse> => {
  const reduxState = store.getState();
  const { token } = reduxState.auth;

  const _requestInit: AxiosRequestConfig = requestInit ?? {};
  const _headers = _requestInit.headers ?? {};
  const _method = _requestInit.method ?? 'GET';
  const url = new URL(path, apiBaseUrl).toString();

  const languageHeader = i18next.language || 'de';
  const cityHeader = cookies.get('city') || SupportedCities.DUSSELDORF;

  // The backend's Passport strategies read the access token with
  // ExtractJwt.fromAuthHeaderAsBearerToken(), so it travels as a standard Authorization header.
  // The refresh token is never sent alongside it — it goes only to POST /customer/refresh.
  if (token) {
    _headers['Authorization'] = `Bearer ${token}`;
  }

  return await apiClient.request({
    ..._requestInit,
    method: _method,
    url,
    timeout: timeout ?? TIMEOUT,
    timeoutErrorMessage: SERVER_NOT_RESPONDING_MSG,
    headers: {
      'Content-Type': 'application/json',
      lang: languageHeader,
      city: sessionStorage.getItem('shippingCity') || cityHeader,
      ..._headers,
    },
    withCredentials: true,
  });
};

export default appRequest;
